import type { DateTimeString } from '@jwplayer/fe-common/module/format/date';

import type { QueryParams } from '../routing/types';

/**
 * Convert key-value based object to a query params string.
 *
 * @example queryParamsToString({ a: 'a b c', b: 123 }); // ?a=a%20b%20c&b=123
 */
export function queryParamsToString(params?: QueryParams): string {
  const parts: string[] = [];

  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (typeof value === 'undefined') {
        continue;
      }

      if (typeof value === 'boolean') {
        parts.push(key);
        continue;
      }

      let stringValue: string = String(value);
      switch (typeof value) {
        case 'string':
          stringValue = value.trim();
          break;
        case 'object':
          stringValue = JSON.stringify(value);
          break;
      }

      if (stringValue) {
        parts.push(`${key}=${encodeURIComponent(stringValue)}`);
      }
    }
  }

  if (parts.length === 0) {
    return '';
  }

  return '?' + parts.join('&');
}

/**
 * Get array of keys from object where value is truthy.
 *
 * @example getTruthyKeys({ a: false, b: '123', c: '', d: true, e: [] }); // ['b', 'd', 'e'];
 */
export function getTruthyKeys(obj: Record<string, unknown>) {
  return Object.entries(obj).reduce(
    (acc, [key, val]) => {
      if (val) {
        acc.push(key);
      }
      return acc;
    },
    [] as (keyof typeof obj)[],
  );
}

/**
 * Download helper function which starts a download using the provided url and file name.
 */
export function downloadFileFromURL(filename: string, url: string, target: '_blank' = '_blank') {
  const a = document.createElement('a');

  a.target = target;
  a.href = url;
  a.download = filename;

  document.body.appendChild(a);

  a.click();
  a.remove();
}

/**
 * Start a download for the provided data (blob) and set the filename + mimetyp.
 */
export function downloadFileFromData(data: BlobPart, filename: string, mimeType: string) {
  // Sanitize filename
  const _filename = filename.replace(/[^\w- ]/g, '');

  const blob = new Blob([data], { type: mimeType });
  const url = URL.createObjectURL(blob);
  return downloadFileFromURL(_filename, url);
}

/**
 * Wrap an async function to allow it to only execute once. If already executed, return its resulting promise object.
 */
export function promiseOnce<T>(fn: () => Promise<T>) {
  let promise: Promise<T> | undefined = undefined;
  return () => {
    if (!promise) {
      promise = fn();
    }

    return promise;
  };
}

/**
 * Create a resumable timer object
 */
export function createTimer({
  callback,
  time,
  pauseOnWindowBlur,
}: {
  /**
   * Callback function when timer ends
   */
  callback: () => void;

  /**
   * Time in milliseconds
   */
  time: number;

  /**
   * Automatically pause on window blur and resume on window focus
   */
  pauseOnWindowBlur?: boolean;
}) {
  let remaining = time;
  let start = 0;
  let timerId = 0;

  function pause() {
    window.clearTimeout(timerId);
    remaining -= Date.now() - start;
  }

  function resume() {
    start = Date.now();
    window.clearTimeout(timerId);
    timerId = window.setTimeout(() => {
      callback();
      clear();
    }, remaining);
  }

  function clear() {
    if (pauseOnWindowBlur) {
      window.removeEventListener('blur', pause);
      window.removeEventListener('focus', resume);
    }
    window.clearTimeout(timerId);
  }

  if (pauseOnWindowBlur) {
    window.addEventListener('blur', pause);
    window.addEventListener('focus', resume);
  }

  resume();

  return { resume, pause, clear };
}

export function isDateInPast(date?: DateTimeString | null) {
  if (!date) {
    return false;
  }
  const d = new Date(date);
  return d.getTime() < Date.now();
}

export function isDateInFuture(date?: DateTimeString | null) {
  if (!date) {
    return false;
  }
  const d = new Date(date);
  return d.getTime() > Date.now();
}

// Escape regex control characters, for matching on a literal string, stored in a variable
// From https://developer.mozilla.org/en-US/docs/Web/JavaScript/Guide/Regular_Expressions#escaping
export function escapeRegExp(str: string) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'); // $& means the whole matched string
}

export const concatStyles = (...args: (string | null | boolean | undefined)[]) => {
  return args.filter(Boolean).join(' ');
};
